import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  description?: string;
  /** Optional in-app route to open when the row is clicked. */
  link?: string;
  /** Stable de-dup key — repeated pushes with the same key replace instead of stacking. */
  dedupeKey?: string;
  createdAt: number;
  read: boolean;
}

interface NotificationsState {
  items: AppNotification[];
  push: (n: Omit<AppNotification, 'id' | 'createdAt' | 'read'> & { id?: string }) => string;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  clear: () => void;
}

const MAX_ITEMS = 50;

function makeId() {
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set) => ({
      items: [],
      push: (n) => {
        const id = n.id ?? makeId();
        let returnedId = id;
        set((s) => {
          const existing = n.dedupeKey
            ? s.items.find((x) => x.dedupeKey === n.dedupeKey)
            : undefined;

          if (existing) {
            returnedId = existing.id;
            // Update in place; preserve read state and createdAt so refreshes
            // don't re-flag the row as unread or float it back to the top.
            return {
              items: s.items.map((x) =>
                x.id === existing.id
                  ? {
                      ...x,
                      type: n.type,
                      title: n.title,
                      description: n.description,
                      link: n.link,
                    }
                  : x,
              ),
            };
          }

          const next = s.items.slice();
          next.unshift({
            id,
            type: n.type,
            title: n.title,
            description: n.description,
            link: n.link,
            dedupeKey: n.dedupeKey,
            createdAt: Date.now(),
            read: false,
          });
          return { items: next.slice(0, MAX_ITEMS) };
        });
        return returnedId;
      },
      markRead: (id) =>
        set((s) => ({ items: s.items.map((x) => (x.id === id ? { ...x, read: true } : x)) })),
      markAllRead: () => set((s) => ({ items: s.items.map((x) => ({ ...x, read: true })) })),
      dismiss: (id) => set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
      clear: () => set({ items: [] }),
    }),
    { name: 'school.notifications' },
  ),
);

export const selectUnreadCount = (s: NotificationsState) =>
  s.items.reduce((acc, n) => acc + (n.read ? 0 : 1), 0);
